import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import { ListaDTO, Parametros } from '../../compartido/utilidades/compartido.models';
import { PilotoDTO, ZonaDTO } from 'src/app/domicilios/utilidades/domicilios.models';
import { AliadoDTO, EmpleadoElevaDTO, RegionDTO } from '../autenticacion/autenticacion.models';
import { ErrorFrontend } from '../utilidades/error.utils';

@Injectable({ providedIn: 'root', deps: [HttpClient] })
export class EmpresaService {
  /* Regiones */
  private _regiones: RegionDTO[] | undefined;
  private _regionesPromise$: Promise<RegionDTO[]> | undefined;
  public get regiones$(): Promise<RegionDTO[]> {
    if (this._regionesPromise$) return this._regionesPromise$;
    if (this._regiones) return Promise.resolve(this._regiones);
    this._regionesPromise$ = this._obtenerRegiones$().then((regiones: RegionDTO[]) => {
      this._regiones = regiones;
      this._regionesPromise$ = undefined;
      return regiones;
    });
    return this._regionesPromise$;
  }

  /* Pilotos */
  private _pilotos: PilotoDTO[] | undefined;
  private _pilotosPromise$: Promise<PilotoDTO[]> | undefined;
  public get pilotos$(): Promise<PilotoDTO[]> {
    if (this._pilotosPromise$) return this._pilotosPromise$;
    if (this._pilotos) return Promise.resolve(this._pilotos);
    this._pilotosPromise$ = this._obtenerPilotos$().then((pilotos: PilotoDTO[]) => {
      this._pilotos = pilotos;
      this._pilotosPromise$ = undefined;
      return pilotos;
    });
    return this._pilotosPromise$;
  }

  /* Aliados */
  private _aliados: AliadoDTO[] | undefined;
  private _aliadosPromise$: Promise<AliadoDTO[]> | undefined;
  public get aliados$(): Promise<AliadoDTO[]> {
    if (this._aliadosPromise$) return this._aliadosPromise$;
    if (this._aliados) return Promise.resolve(this._aliados);
    this._aliadosPromise$ = this._obtenerAliados$().then((aliados: AliadoDTO[]) => {
      this._aliados = aliados;
      this._aliadosPromise$ = undefined;
      return aliados;
    });
    return this._aliadosPromise$;
  }

  constructor(private readonly http: HttpClient) {}

  /**
   * Obtiene la lista de regiones del servidor.
   * @returns {Promise<RegionDTO[]>} - La lista de regiones como llega del servidor (promise).
   * @author Juan Corral
   */
  private _obtenerRegiones$(): Promise<RegionDTO[]> {
    return firstValueFrom(
      this.http.get<ListaDTO<RegionDTO>>('empresa/regiones/').pipe(
        map((res: ListaDTO<RegionDTO>) => res.results),
        catchError(() => of([])),
      ),
    );
  }

  /**
   * Obtiene la lista de pilotos del servidor.
   * @returns {Promise<PilotoDTO[]>} - La lista de pilotos como llega del servidor (promise).
   * @author Juan Corral
   */
  private _obtenerPilotos$(): Promise<PilotoDTO[]> {
    return firstValueFrom(
      this.http.get<ListaDTO<PilotoDTO>>('empresa/pilotos/').pipe(
        map((res: ListaDTO<PilotoDTO>) => res.results),
        catchError(() => of([])),
      ),
    );
  }

  /**
   * Obtiene la lista de aliados del servidor.
   * @returns {Promise<AliadoDTO[]>} - La lista de aliados como llega del servidor (promise).
   * @author Juan Corral
   */
  private _obtenerAliados$(): Promise<AliadoDTO[]> {
    return firstValueFrom(
      this.http.get<ListaDTO<AliadoDTO>>('empresa/aliados/').pipe(
        map((res: ListaDTO<AliadoDTO>) => res.results),
        catchError(() => of([])),
      ),
    );
  }

  /**
   * Obtiene la lista de empleados Eleva del servidor.
   * @param {Parametros} filtros - Filtros para la consulta.
   * @returns {Promise<ListaDTO<EmpleadoElevaDTO>>} - La lista de empleados Eleva como llega del servidor (promise).
   * @throws {ErrorFrontend} - Si ocurre un error en la solicitud.
   * @author Juan Corral
   */
  public obtenerEmpleadosEleva$(filtros: Parametros): Promise<ListaDTO<EmpleadoElevaDTO>> {
    return firstValueFrom(
      this.http.get<ListaDTO<EmpleadoElevaDTO>>('empresa/empleados-eleva/', {
        params: filtros,
      }),
    ).catch((error: HttpErrorResponse) => {
      throw new ErrorFrontend(error);
    });
  }

  /**
   * Limpia los datos almacenados en el servicio.
   * @author Juan Corral
   */
  public limpiar(): void {
    this._pilotos = undefined;
    this._pilotosPromise$ = undefined;
    this._aliados = undefined;
    this._aliadosPromise$ = undefined;
    this._regiones = undefined;
    this._regionesPromise$ = undefined;
  }

  /**
   * Obtiene las zonas de una región.
   * @param {number} regionId - El id de la región.
   * @returns {Promise<ListaDTO<ZonaDTO>>} - Las zonas de la región (promise).
   * @throws {ErrorFrontend} - Si hubo un error al obtener las zonas.
   * @author Juan Corral
   */
  public obtenerZonas$(regionId: number): Promise<ListaDTO<ZonaDTO>> {
    return firstValueFrom(
      this.http.get<ListaDTO<ZonaDTO>>('empresa/zonas/', { params: { region: regionId } }).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Asigna pilotos a una zona.
   * @param {number} zonaId - El id de la zona.
   * @param {number[]} pilotos - Los ids de los pilotos a asignar.
   * @throws {ErrorFrontend} - Si hubo un error al asignar los pilotos.
   * @author Juan Corral
   */
  public async asignarPilotosZona$(zonaId: number, pilotos: number[]): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(`empresa/zonas/${zonaId}/asignar_pilotos/`, { pilotos }).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Da de baja a un piloto. Falla si el piloto tiene saldo pendiente en caja.
   * @param {number} pilotoId - El id del piloto a dar de baja.
   * @throws {ErrorFrontend} - Si hubo un error al dar de baja al piloto.
   * @author Daniel Peralta
   */
  public async darBajaPiloto$(pilotoId: number): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(`empresa/pilotos/${pilotoId}/dar_baja/`, {}).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Reactiva a un piloto previamente dado de baja.
   * @param {number} pilotoId - El id del piloto a reactivar.
   * @throws {ErrorFrontend} - Si hubo un error al reactivar al piloto.
   * @author Daniel Peralta
   */
  public async reactivarPiloto$(pilotoId: number): Promise<void> {
    await firstValueFrom(
      this.http.post<void>(`empresa/pilotos/${pilotoId}/reactivar/`, {}).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }
}
